<template>
  <div class="feed-container">
    <div class="calendar-wrapper" v-if="profile.status === 'Available'">
      <v-date-picker
        v-model="picker"
        full-width
        elevation="2"
        color="#084468"
        :events="needsDays"
        event-color="amber"
        :loading="monthLoading"
        @update:picker-date="onMonthChange"
      >
        <template>
          <div class="calendar-header">
            <div class="calendar-title-wrapper">
              <h3 class="calendar-title">{{ currentMonthYear }}</h3>
              <v-progress-circular
                v-if="monthLoading"
                indeterminate
                size="20"
                width="2"
                color="white"
                class="ml-2"
              />
            </div>
          </div>
        </template>
      </v-date-picker>
    </div>

    <div class="needs-list" v-if="profile.status === 'Available'">
      <template v-if="!loading">
        <agency-need-card
          v-for="need in needs"
          :key="need._id"
          :need="need"
          class="need-card mb-3"
          :editable="false"
        />

        <div v-if="needs.length == 0" class="empty-state mt-6">
          <v-img
            src="@/assets/svg/no-needCalendar.svg"
            height="400"
            contain
            alt="No needs for selected date"
          />
        </div>
      </template>

      <div v-else class="loading-needs">
        <v-progress-circular indeterminate color="#fff" size="54" width="6" />
        <div class="mt-4" style="color:#fff">Loading needs...</div>
      </div>
    </div>
    <div
      v-else-if="profile.status === 'Pending' || profile.status === 'Rejected'"
      class="status-message"
    >
      <v-card class="status-card">
        <v-card-text class="text-center py-8">
          <v-icon size="64" color="warning">mdi-clock</v-icon>
          <h2 class="mt-4">Account Under Review</h2>
          <p class="mt-2 grey--text">
            Please wait for approval/confirmation from Admin
          </p>
          <v-btn color="primary" text class="mt-4" @click="$router.push('/')">
            Back to Home
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
    <v-snackbar v-model="snackbar">
      {{ errorMessage }}
      <v-btn text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AgencyNeedCard from "../agency/needs/AgencyNeedCard.vue";

export default {
  components: { AgencyNeedCard },
  data() {
    return {
      loading: false, // For date needs loading
      monthLoading: false, // For month highlights loading
      errorMessage: null,
      snackbar: false,
      picker: new Date().toISOString().substr(0, 10),
      needs: [],
      needsDays: []
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile"
    }),
    currentMonthYear() {
      const date = new Date(this.picker);
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric"
      });
    }
  },
  watch: {
    picker(newValue, oldValue) {
      console.log("picker changed", newValue, oldValue);
      if (newValue) {
        this.loadNeeds();
      }
      if (oldValue) {
        const newYearMonth = newValue.substring(0, 7);
        const oldYearMonth = oldValue.substring(0, 7);

        if (newYearMonth !== oldYearMonth) {
          this.loadMonthHighlights(newYearMonth);
        }
      } else {
        this.loadMonthHighlights(newValue.substring(0, 7));
      }
    }
  },
  methods: {
    ...mapActions("need", {
      getDateNeeds: "fetchDateNeeds",
      getMonthNeeds: "fetchMonthNeeds"
    }),

    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },

    loadNeeds() {
      this.loading = true;
      this.getDateNeeds(this.picker)
        .then(res => {
          this.needs = res;
        })
        .catch(error => {
          if (error.response == undefined) {
            this.showErrorMessage(error);
          } else {
            this.showErrorMessage(error.response.data.message);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadMonthHighlights(month) {
      this.monthLoading = true;
      this.getMonthNeeds(month)
        .then(res => {
          this.needsDays = Array.from(new Set([...this.needsDays, ...res]));
        })
        .catch(error => {
          console.error("Error loading month highlights:", error);
        })
        .finally(() => {
          this.monthLoading = false;
        });
    },

    onMonthChange(newDate) {
      console.log("Month changed:", newDate);
      const newYearMonth = newDate.substring(0, 7);
      const currentYearMonth = this.picker.substring(0, 7);

      if (newYearMonth !== currentYearMonth) {
        console.log("Month changed:", newYearMonth);
        this.loadMonthHighlights(newYearMonth);
      }
    },
    nextMonth() {
      console.log('nextMonth');
      const d = new Date(this.date);
      d.setMonth(d.getMonth() + 1);
      this.date = d.toISOString().substr(0, 10);
    },
    prevMonth() {
      console.log('prevMonth');
      const d = new Date(this.date);
      d.setMonth(d.getMonth() - 1);
      this.date = d.toISOString().substr(0, 10);
    }
  },
  mounted() {
    this.loadNeeds();
    this.loadMonthHighlights(this.picker.substring(0, 7));
  }
};
</script>

<style scoped>
.feed-container {
  width: 100%;
  max-width: 650px;
  margin: 0 auto;
  padding: 20px;
}

.calendar-wrapper {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: var(--v-primary-base);
  color: white;
}

.calendar-title-wrapper {
  display: flex;
  align-items: center;
}

.calendar-title {
  margin: 0;
  font-weight: 500;
}

.status-message {
  width: 100%;
  max-width: 600px;
  margin-top: 60px;
}
.status-card {
  background: #fff;
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
}
.status-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 600;
  justify-content: center;
}

.status-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-top: 16px;
}

.needs-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 200px;
}

.empty-state {
  color: white;
}

.need-card {
  width: 100%;
}

.loading-needs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
  color: rgba(0, 0, 0, 0.6);
}

.date-picker :deep(.v-picker__title) {
  background: #0a5b8a !important;
  color: white;
  font-weight: 700;
  font-family: Poppins-Bold;
}
</style>
